import { useState, useEffect, useMemo, createElement } from 'react';
import { Link } from 'react-router-dom';
import { X, TruckIcon, Shield, Award, Megaphone, Gift } from 'lucide-react';
import { siteLinksAPI } from '../services/api';

// Pick an icon from a slide's text. Keeps the banner visually varied without
// requiring the admin to manage icons in the database.
const pickIcon = (text) => {
  const t = (text || '').toLowerCase();
  if (/(ship|deliver|free)/.test(t)) return TruckIcon;
  if (/(warrant|guarantee|refund|money[- ]back)/.test(t)) return Shield;
  if (/(engineer|install|plug|play|quality|made)/.test(t)) return Award;
  if (/(discount|sale|off|promo|code|coupon)/.test(t)) return Gift;
  return Megaphone;
};

const isExternal = (url) => /^https?:\/\//i.test(url || '');

const SlideContent = ({ slide }) => {
  const url = (slide.url || '').trim();
  if (!url) return <>{slide.label}</>;
  if (isExternal(url)) {
    return (
      <a
        href={url}
        target={slide.target === '_blank' ? '_blank' : undefined}
        rel={slide.target === '_blank' ? 'noopener noreferrer' : undefined}
        className="underline underline-offset-2 hover:text-blue-300"
        data-testid={`banner-slide-link-${slide.id}`}
      >
        {slide.label}
      </a>
    );
  }
  return (
    <Link
      to={url}
      target={slide.target === '_blank' ? '_blank' : undefined}
      className="underline underline-offset-2 hover:text-blue-300"
      data-testid={`banner-slide-link-${slide.id}`}
    >
      {slide.label}
    </Link>
  );
};

const SlideRow = ({ slide }) => (
  <div className="flex items-center space-x-3 animate-fade-in">
    {createElement(pickIcon(slide.label), { className: 'h-4 w-4 flex-shrink-0' })}
    <p
      className="text-sm font-semibold tracking-wide text-center uppercase"
      data-testid={`banner-slide-${slide.id}`}
    >
      <SlideContent slide={slide} />
    </p>
  </div>
);

const AnnouncementBanner = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState([]);

  useEffect(() => {
    siteLinksAPI.getPublic()
      .then((d) => setSlides(d?.banner || []))
      .catch(() => setSlides([]));
  }, []);

  const safeSlides = useMemo(() => slides.filter((s) => s.label?.trim()), [slides]);

  useEffect(() => {
    if (safeSlides.length <= 1) return undefined;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % safeSlides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [safeSlides.length]);

  if (!isVisible || safeSlides.length === 0) return null;

  const slide = safeSlides[currentSlide % safeSlides.length];

  return (
    <div
      className="bg-black text-blue-400 py-2.5 px-4 relative border-b border-white/10"
      data-testid="announcement-banner"
    >
      <div className="container mx-auto flex items-center justify-center">
        <SlideRow slide={slide} />
        <button
          onClick={() => setIsVisible(false)}
          className="absolute right-4 p-1 hover:bg-white/10 text-blue-300 hover:text-white rounded transition-colors"
          aria-label="Close banner"
          data-testid="announcement-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Slide indicators */}
      {safeSlides.length > 1 && (
        <div className="flex justify-center space-x-1.5 mt-1.5">
          {safeSlides.map((s, index) => (
            <button
              key={s.id}
              onClick={() => setCurrentSlide(index)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'w-6 bg-blue-400' : 'w-1.5 bg-blue-400/40'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AnnouncementBanner;
